// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ChevronDown, ChevronUp, Lock, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { MaterialIconPicker } from "@/features/tasks/material-icon-picker";
import type { TaskCardSection } from "@/features/tasks/task-card-preview/task-card-preview";
import { isInlineSvgIcon } from "@/features/tasks/task-icon";
import {
  ACTION_SUPPRESSIONS,
  FIELD_COLORS,
  FIELD_TYPES,
  FIELD_VARIANTS,
  HEADER_MODES,
  type InfoTabField,
  type SnoozeConfig,
  SUB_HEADER_MODES,
  TAB_CONTENT_TYPES,
  type TaskActionConfig,
  type TaskCardSettings,
  type TaskCardTab,
  type TopContentField,
  tabContentType,
  tabKey,
} from "@/features/tasks/types";
import { cn } from "@/lib/utils";

/**
 * The editor panels behind each region of the card preview.
 *
 * Every list editor mutates entries by spreading the original object, so keys the back office does
 * not model — the Action Tool adds them faster than we learn about them — survive a round-trip
 * untouched instead of being silently dropped on save.
 */

interface SectionProps {
  settings: TaskCardSettings;
  onChange: (patch: Partial<TaskCardSettings>) => void;
  disabled?: boolean;
  /** Card fields the current chain/store scope pins away from the global value. */
  overriddenFields: string[];
  /** True when editing the global card — the only scope where every field is writable. */
  isGlobalScope: boolean;
}

/* -------------------------------------------------------------------------- */
/* Field primitives                                                            */
/* -------------------------------------------------------------------------- */

function FieldRow({
  label,
  htmlFor,
  overridden,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  overridden?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("back-office");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={htmlFor}>{label}</Label>
        {overridden && (
          <Badge variant="secondary" className="h-4 px-1 text-[10px] font-normal">
            {t("tasks.editor.overridden")}
          </Badge>
        )}
      </div>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ColorInput({
  value,
  onChange,
  disabled,
  id,
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        id={id}
        aria-label={id}
        value={value || "#000000"}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-12 cursor-pointer rounded border bg-transparent p-1 disabled:cursor-not-allowed"
      />
      <Input
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="font-mono"
        placeholder="#000000"
      />
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <div className="min-w-0">
        <Label className="font-normal">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/** A read-only banner for fields that have no override column and so are global-only. */
function GlobalOnlyNotice({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
      <Lock className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

/** Move an item within a list, returning a new array. */
function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  if (item === undefined) return items;
  next.splice(target, 0, item);
  return next;
}

function ListControls({
  index,
  length,
  onMove,
  onRemove,
  disabled,
}: {
  index: number;
  length: number;
  onMove: (delta: number) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("back-office");
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={disabled || index === 0}
        aria-label={t("tasks.editor.moveUp")}
        onClick={() => onMove(-1)}
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={disabled || index === length - 1}
        aria-label={t("tasks.editor.moveDown")}
        onClick={() => onMove(1)}
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:text-destructive"
        disabled={disabled}
        aria-label={t("tasks.editor.remove")}
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

function IdentitySection({ settings, onChange, disabled, overriddenFields }: SectionProps) {
  const { t } = useTranslation("back-office");
  const pinned = (field: string) => overriddenFields.includes(field);

  return (
    <div className="space-y-4">
      <FieldRow label={t("tasks.fields.fullName")} htmlFor="task-full-name" overridden={pinned("fullName")}>
        <Input
          id="task-full-name"
          value={settings.fullName}
          disabled={disabled}
          onChange={(event) => onChange({ fullName: event.target.value })}
        />
      </FieldRow>
      <FieldRow label={t("tasks.fields.shortName")} htmlFor="task-short-name" overridden={pinned("shortName")}>
        <Input
          id="task-short-name"
          value={settings.shortName ?? ""}
          disabled={disabled}
          onChange={(event) => onChange({ shortName: event.target.value || null })}
        />
      </FieldRow>
      <FieldRow
        label={t("tasks.fields.acronym")}
        htmlFor="task-acronym"
        overridden={pinned("acronym")}
        hint={t("tasks.editor.acronymHint")}
      >
        <Input
          id="task-acronym"
          maxLength={6}
          value={settings.acronym ?? ""}
          disabled={disabled}
          onChange={(event) => onChange({ acronym: event.target.value || null })}
        />
      </FieldRow>
      <Separator />
      <FieldRow
        label={t("tasks.fields.algoFillAction")}
        htmlFor="task-algo-fill"
        overridden={pinned("algoFillAction")}
        hint={t("tasks.editor.algoFillHint")}
      >
        <Input
          id="task-algo-fill"
          value={settings.algoFillAction}
          disabled={disabled}
          className="font-mono"
          onChange={(event) => onChange({ algoFillAction: event.target.value })}
        />
      </FieldRow>
      <FieldRow
        label={t("tasks.fields.serviceUserId")}
        htmlFor="task-service-user"
        overridden={pinned("serviceUserId")}
        hint={t("tasks.editor.serviceUserHint")}
      >
        <Input
          id="task-service-user"
          type="number"
          value={settings.serviceUserId ?? ""}
          disabled={disabled}
          onChange={(event) => onChange({ serviceUserId: event.target.value ? Number(event.target.value) : null })}
        />
      </FieldRow>
      <SwitchRow
        label={t("tasks.fields.pickable")}
        hint={t("tasks.editor.pickableHint")}
        checked={settings.isPickable}
        disabled={disabled}
        onChange={(checked) => onChange({ isPickable: checked })}
      />
    </div>
  );
}

function HeaderSection({ settings, onChange, disabled, overriddenFields }: SectionProps) {
  const { t } = useTranslation("back-office");
  const pinned = (field: string) => overriddenFields.includes(field);
  const usesText = settings.headerMode === "text" || settings.headerMode === "template";

  return (
    <div className="space-y-4">
      <FieldRow label={t("tasks.fields.headerMode")} overridden={pinned("headerMode")}>
        <Select
          value={settings.headerMode}
          disabled={disabled}
          onValueChange={(value) => onChange({ headerMode: value as TaskCardSettings["headerMode"] })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HEADER_MODES.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {t(`tasks.headerMode.${mode}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
      {usesText && (
        <FieldRow
          label={t("tasks.fields.headerText")}
          htmlFor="task-header-text"
          overridden={pinned("headerText")}
          hint={settings.headerMode === "template" ? t("tasks.editor.templateHint") : undefined}
        >
          <Textarea
            id="task-header-text"
            rows={2}
            value={settings.headerText ?? ""}
            disabled={disabled}
            onChange={(event) => onChange({ headerText: event.target.value || null })}
          />
        </FieldRow>
      )}
      <Separator />
      <FieldRow label={t("tasks.fields.color")} overridden={pinned("color")}>
        <ColorInput value={settings.color} disabled={disabled} onChange={(color) => onChange({ color })} />
      </FieldRow>
      <FieldRow label={t("tasks.fields.headerFontColor")} overridden={pinned("headerFontColor")}>
        <ColorInput
          value={settings.headerFontColor}
          disabled={disabled}
          onChange={(headerFontColor) => onChange({ headerFontColor })}
        />
      </FieldRow>
      <Separator />
      <FieldRow label={t("tasks.fields.icon")} overridden={pinned("icon")} hint={t("tasks.editor.iconHint")}>
        {isInlineSvgIcon(settings.icon) ? (
          <div className="space-y-2">
            <Textarea
              rows={4}
              value={settings.icon ?? ""}
              disabled={disabled}
              className="font-mono text-xs"
              onChange={(event) => onChange({ icon: event.target.value || null })}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onChange({ icon: "" })}
            >
              {t("tasks.editor.icon.switchToNamed")}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <MaterialIconPicker value={settings.icon} disabled={disabled} onChange={(icon) => onChange({ icon })} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onChange({ icon: '<path d="" />' })}
            >
              {t("tasks.editor.icon.switchToSvg")}
            </Button>
          </div>
        )}
      </FieldRow>
    </div>
  );
}

function SubHeaderSection({ settings, onChange, disabled, overriddenFields }: SectionProps) {
  const { t } = useTranslation("back-office");
  const pinned = (field: string) => overriddenFields.includes(field);
  const usesText = settings.subHeaderMode === "text" || settings.subHeaderMode === "template";

  return (
    <div className="space-y-4">
      <FieldRow label={t("tasks.fields.subHeaderMode")} overridden={pinned("subHeaderMode")}>
        <Select
          value={settings.subHeaderMode ?? "location"}
          disabled={disabled}
          onValueChange={(value) => onChange({ subHeaderMode: value as TaskCardSettings["subHeaderMode"] })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUB_HEADER_MODES.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {t(`tasks.subHeaderMode.${mode}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
      {usesText && (
        <FieldRow
          label={t("tasks.fields.subHeaderText")}
          htmlFor="task-sub-header-text"
          overridden={pinned("subHeaderText")}
          hint={settings.subHeaderMode === "template" ? t("tasks.editor.templateHint") : undefined}
        >
          <Textarea
            id="task-sub-header-text"
            rows={2}
            value={settings.subHeaderText ?? ""}
            disabled={disabled}
            onChange={(event) => onChange({ subHeaderText: event.target.value || null })}
          />
        </FieldRow>
      )}
      <Separator />
      <FieldRow
        label={t("tasks.fields.subHeaderColor")}
        overridden={pinned("subHeaderColor")}
        hint={t("tasks.editor.subHeaderColorHint")}
      >
        <ColorInput
          value={settings.subHeaderColor}
          disabled={disabled}
          onChange={(subHeaderColor) => onChange({ subHeaderColor })}
        />
      </FieldRow>
      <FieldRow label={t("tasks.fields.subHeaderFontColor")} overridden={pinned("subHeaderFontColor")}>
        <ColorInput
          value={settings.subHeaderFontColor}
          disabled={disabled}
          onChange={(subHeaderFontColor) => onChange({ subHeaderFontColor })}
        />
      </FieldRow>
      <Separator />
      <FieldRow
        label={t("tasks.fields.locationStatusColor")}
        overridden={pinned("locationStatusColor")}
        hint={t("tasks.editor.locationStatusHint")}
      >
        <ColorInput
          value={settings.locationStatus.color}
          disabled={disabled}
          onChange={(color) => onChange({ locationStatus: { ...settings.locationStatus, color } })}
        />
      </FieldRow>
      <FieldRow label={t("tasks.fields.locationStatusFontColor")} overridden={pinned("locationStatusFontColor")}>
        <Select
          value={settings.locationStatus.textColor ?? "light"}
          disabled={disabled}
          onValueChange={(textColor) => onChange({ locationStatus: { ...settings.locationStatus, textColor } })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">{t("tasks.editor.light")}</SelectItem>
            <SelectItem value="dark">{t("tasks.editor.dark")}</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>
    </div>
  );
}

/** Shared editor for the two field lists — they differ only in whether `gridSize` applies. */
function FieldListEditor({
  fields,
  onChange,
  disabled,
  withGridSize,
}: {
  fields: (InfoTabField | TopContentField)[];
  onChange: (fields: (InfoTabField | TopContentField)[]) => void;
  disabled?: boolean;
  withGridSize: boolean;
}) {
  const { t } = useTranslation("back-office");

  function patch(index: number, changes: Record<string, unknown>) {
    onChange(fields.map((field, i) => (i === index ? { ...field, ...changes } : field)));
  }

  return (
    <div className="space-y-3">
      {fields.map((field, index) => (
        <div key={`${field.field}-${index}`} className="space-y-2 rounded-md border p-3">
          <div className="flex items-start gap-2">
            <div className="grid flex-1 grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">{t("tasks.editor.field.path")}</Label>
                <Input
                  value={field.field}
                  disabled={disabled}
                  className="font-mono text-xs"
                  onChange={(event) => patch(index, { field: event.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("tasks.editor.field.label")}</Label>
                <Input
                  value={field.label}
                  disabled={disabled}
                  className="text-xs"
                  onChange={(event) => patch(index, { label: event.target.value })}
                />
              </div>
            </div>
            <ListControls
              index={index}
              length={fields.length}
              disabled={disabled}
              onMove={(delta) => onChange(moveItem(fields, index, delta))}
              onRemove={() => onChange(fields.filter((_, i) => i !== index))}
            />
          </div>
          <div className={cn("grid gap-2", withGridSize ? "grid-cols-4" : "grid-cols-3")}>
            <div className="space-y-1">
              <Label className="text-xs">{t("tasks.editor.field.type")}</Label>
              <Select value={field.type} disabled={disabled} onValueChange={(type) => patch(index, { type })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("tasks.editor.field.variant")}</Label>
              <Select
                value={field.variant ?? "standard"}
                disabled={disabled}
                onValueChange={(variant) => patch(index, { variant })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_VARIANTS.map((variant) => (
                    <SelectItem key={variant} value={variant}>
                      {variant}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("tasks.editor.field.color")}</Label>
              <Select
                value={(field.color as string) ?? "info"}
                disabled={disabled}
                onValueChange={(color) => patch(index, { color })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_COLORS.map((color) => (
                    <SelectItem key={color} value={color}>
                      {color}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {withGridSize && (
              <div className="space-y-1">
                <Label className="text-xs">{t("tasks.editor.field.gridSize")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={(field as TopContentField).gridSize ?? 12}
                  disabled={disabled}
                  className="h-8 text-xs"
                  onChange={(event) => patch(index, { gridSize: Number(event.target.value) })}
                />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("tasks.editor.field.chains")}</Label>
            <Input
              value={(field.chains ?? []).join(", ")}
              disabled={disabled}
              placeholder={t("tasks.editor.field.chainsPlaceholder")}
              className="font-mono text-xs"
              onChange={(event) => {
                const chains = event.target.value
                  .split(",")
                  .map((chain) => chain.trim())
                  .filter(Boolean);
                patch(index, { chains: chains.length ? chains : undefined });
              }}
            />
          </div>
        </div>
      ))}
      {!fields.length && <p className="text-sm italic text-muted-foreground">{t("tasks.editor.field.empty")}</p>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...fields, { type: "text", field: "item.name", label: "label", variant: "outlined" }])}
      >
        <Plus className="h-4 w-4" />
        {t("tasks.editor.field.add")}
      </Button>
    </div>
  );
}

function TabsSection({ settings, onChange, disabled, overriddenFields }: SectionProps) {
  const { t } = useTranslation("back-office");

  function patch(index: number, tab: TaskCardTab) {
    onChange({ tabs: settings.tabs.map((existing, i) => (i === index ? tab : existing)) });
  }

  return (
    <div className="space-y-3">
      {overriddenFields.includes("tabs") && (
        <Badge variant="secondary" className="font-normal">
          {t("tasks.editor.overridden")}
        </Badge>
      )}
      {settings.tabs.map((tab, index) => {
        const isObject = typeof tab !== "string";
        return (
          <div key={`${tabKey(tab)}-${index}`} className="space-y-2 rounded-md border p-3">
            <div className="flex items-start gap-2">
              <div className="grid flex-1 grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t("tasks.editor.tab.contentType")}</Label>
                  <Select
                    value={tabContentType(tab)}
                    disabled={disabled}
                    onValueChange={(contentType) =>
                      patch(index, isObject ? { ...tab, contentType: contentType as never } : contentType)
                    }
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAB_CONTENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isObject && (
                  <div className="space-y-1">
                    <Label className="text-xs">{t("tasks.editor.tab.name")}</Label>
                    <Input
                      value={tab.name}
                      disabled={disabled}
                      className="font-mono text-xs"
                      onChange={(event) => patch(index, { ...tab, name: event.target.value })}
                    />
                  </div>
                )}
              </div>
              <ListControls
                index={index}
                length={settings.tabs.length}
                disabled={disabled}
                onMove={(delta) => onChange({ tabs: moveItem(settings.tabs, index, delta) })}
                onRemove={() => onChange({ tabs: settings.tabs.filter((_, i) => i !== index) })}
              />
            </div>
            {isObject && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t("tasks.editor.tab.labelDefault")}</Label>
                  <Input
                    value={tab.tabLabels?.default ?? ""}
                    disabled={disabled}
                    className="text-xs"
                    onChange={(event) =>
                      patch(index, {
                        ...tab,
                        tabLabels: { abbr: tab.tabLabels?.abbr ?? "", default: event.target.value },
                      })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("tasks.editor.tab.labelAbbr")}</Label>
                  <Input
                    value={tab.tabLabels?.abbr ?? ""}
                    disabled={disabled}
                    className="text-xs"
                    onChange={(event) =>
                      patch(index, {
                        ...tab,
                        tabLabels: { default: tab.tabLabels?.default ?? "", abbr: event.target.value },
                      })
                    }
                  />
                </div>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() =>
                patch(
                  index,
                  isObject
                    ? tabContentType(tab)
                    : { name: tabContentType(tab), contentType: tabContentType(tab) as never },
                )
              }
            >
              {isObject ? t("tasks.editor.tab.simplify") : t("tasks.editor.tab.configure")}
            </Button>
          </div>
        );
      })}
      {!settings.tabs.length && <p className="text-sm italic text-muted-foreground">{t("tasks.editor.tab.empty")}</p>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange({ tabs: [...settings.tabs, "info"] })}
      >
        <Plus className="h-4 w-4" />
        {t("tasks.editor.tab.add")}
      </Button>
    </div>
  );
}

/** Editor for one of the two action lists. */
function ActionListEditor({
  actions,
  onChange,
  disabled,
}: {
  actions: TaskActionConfig[];
  onChange: (actions: TaskActionConfig[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("back-office");

  function patch(index: number, changes: Partial<TaskActionConfig>) {
    onChange(actions.map((action, i) => (i === index ? { ...action, ...changes } : action)));
  }

  return (
    <div className="space-y-3">
      {actions.map((action, index) => (
        <div key={`${action.type}-${index}`} className="space-y-2 rounded-md border p-3">
          <div className="flex items-start gap-2">
            <div className="grid flex-1 grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">{t("tasks.editor.action.type")}</Label>
                <Input
                  value={action.type}
                  disabled={disabled}
                  className="font-mono text-xs"
                  onChange={(event) => patch(index, { type: event.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("tasks.editor.action.displayName")}</Label>
                <Input
                  value={action.displayName}
                  disabled={disabled}
                  className="text-xs"
                  onChange={(event) => patch(index, { displayName: event.target.value })}
                />
              </div>
            </div>
            <ListControls
              index={index}
              length={actions.length}
              disabled={disabled}
              onMove={(delta) => onChange(moveItem(actions, index, delta))}
              onRemove={() => onChange(actions.filter((_, i) => i !== index))}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("tasks.editor.action.suppression")}</Label>
              <Select
                value={action.suppression ?? "NONE"}
                disabled={disabled}
                onValueChange={(suppression) => patch(index, { suppression: suppression as never })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_SUPPRESSIONS.map((suppression) => (
                    <SelectItem key={suppression} value={suppression}>
                      {t(`tasks.suppression.${suppression}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("tasks.editor.action.label")}</Label>
              <Input
                value={action.label ?? ""}
                disabled={disabled}
                className="font-mono text-xs"
                onChange={(event) => patch(index, { label: event.target.value || undefined })}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6">
            <SwitchRow
              label={t("tasks.editor.action.confirmationRequired")}
              checked={!!action.confirmationRequired}
              disabled={disabled}
              onChange={(checked) => patch(index, { confirmationRequired: checked || undefined })}
            />
            <SwitchRow
              label={t("tasks.editor.action.inventoryUpdateRequired")}
              checked={!!action.inventoryUpdateRequired}
              disabled={disabled}
              onChange={(checked) => patch(index, { inventoryUpdateRequired: checked || undefined })}
            />
          </div>
        </div>
      ))}
      {!actions.length && <p className="text-sm italic text-muted-foreground">{t("tasks.editor.action.empty")}</p>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...actions, { type: "APPROVED", displayName: "Fixed", suppression: "EOD" }])}
      >
        <Plus className="h-4 w-4" />
        {t("tasks.editor.action.add")}
      </Button>
    </div>
  );
}

function ActionsSection({ settings, onChange, disabled, overriddenFields, isGlobalScope }: SectionProps) {
  const { t } = useTranslation("back-office");
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">{t("tasks.editor.workedActions")}</h4>
          {overriddenFields.includes("workedActions") && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-normal">
              {t("tasks.editor.overridden")}
            </Badge>
          )}
        </div>
        <ActionListEditor
          actions={settings.workedActions}
          disabled={disabled}
          onChange={(workedActions) => onChange({ workedActions })}
        />
      </div>
      <Separator />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">{t("tasks.editor.notWorkedActions")}</h4>
          {overriddenFields.includes("notWorkedActions") && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-normal">
              {t("tasks.editor.overridden")}
            </Badge>
          )}
        </div>
        <ActionListEditor
          actions={settings.notWorkedActions}
          disabled={disabled}
          onChange={(notWorkedActions) => onChange({ notWorkedActions })}
        />
      </div>
      <Separator />
      <div className="space-y-2">
        <h4 className="text-sm font-medium">{t("tasks.editor.startActions")}</h4>
        {isGlobalScope ? (
          <ActionListEditor
            actions={settings.startActions as TaskActionConfig[]}
            disabled={disabled}
            onChange={(startActions) => onChange({ startActions: startActions as never })}
          />
        ) : (
          <GlobalOnlyNotice text={t("tasks.editor.startActionsGlobalOnly")} />
        )}
      </div>
    </div>
  );
}

function SnoozeSection({ settings, onChange, disabled, overriddenFields }: SectionProps) {
  const { t } = useTranslation("back-office");
  const options = settings.snoozeOptions;

  function patch(index: number, changes: Partial<SnoozeConfig>) {
    onChange({ snoozeOptions: options.map((option, i) => (i === index ? { ...option, ...changes } : option)) });
  }

  return (
    <div className="space-y-3">
      {overriddenFields.includes("snoozeOptions") && (
        <Badge variant="secondary" className="font-normal">
          {t("tasks.editor.overridden")}
        </Badge>
      )}
      <SwitchRow
        label={t("tasks.editor.enableSnoozeButton")}
        checked={settings.enableSnoozeButton}
        disabled={disabled}
        onChange={(checked) => onChange({ enableSnoozeButton: checked })}
      />
      <SwitchRow
        label={t("tasks.editor.enableSnoozeSetTimeButton")}
        checked={settings.enableSnoozeSetTimeButton}
        disabled={disabled}
        onChange={(checked) => onChange({ enableSnoozeSetTimeButton: checked })}
      />
      <Separator />
      {options.map((option, index) => (
        <div key={`${option.displayName}-${index}`} className="flex items-end gap-2 rounded-md border p-3">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">{t("tasks.editor.snooze.displayName")}</Label>
            <Input
              value={option.displayName}
              disabled={disabled}
              className="text-xs"
              onChange={(event) => patch(index, { displayName: event.target.value })}
            />
          </div>
          <div className="w-28 space-y-1">
            <Label className="text-xs">{t("tasks.editor.snooze.minutes")}</Label>
            <Input
              type="number"
              min={1}
              value={option.minutes}
              disabled={disabled}
              className="text-xs"
              onChange={(event) => patch(index, { minutes: Number(event.target.value) })}
            />
          </div>
          <ListControls
            index={index}
            length={options.length}
            disabled={disabled}
            onMove={(delta) => onChange({ snoozeOptions: moveItem(options, index, delta) })}
            onRemove={() => onChange({ snoozeOptions: options.filter((_, i) => i !== index) })}
          />
        </div>
      ))}
      {!options.length && <p className="text-sm italic text-muted-foreground">{t("tasks.editor.snooze.empty")}</p>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange({ snoozeOptions: [...options, { displayName: "30m", minutes: 30 }] })}
      >
        <Plus className="h-4 w-4" />
        {t("tasks.editor.snooze.add")}
      </Button>
    </div>
  );
}

function TogglesSection({ settings, onChange, disabled }: SectionProps) {
  const { t } = useTranslation("back-office");
  const toggles: { key: keyof TaskCardSettings; label: string; hint?: string }[] = [
    { key: "alwaysShowOosImage", label: t("tasks.toggles.alwaysShowOosImage") },
    { key: "showProductImages", label: t("tasks.toggles.showProductImages") },
    { key: "alwaysExpandItemInfo", label: t("tasks.toggles.alwaysExpandItemInfo") },
    { key: "enableCompress", label: t("tasks.toggles.enableCompress"), hint: t("tasks.toggles.enableCompressHint") },
    { key: "enableInventoryButton", label: t("tasks.toggles.enableInventoryButton") },
    { key: "enableDeleteTaskButton", label: t("tasks.toggles.enableDeleteTaskButton") },
    {
      key: "disableActionAutoselect",
      label: t("tasks.toggles.disableActionAutoselect"),
      hint: t("tasks.toggles.disableActionAutoselectHint"),
    },
  ];

  return (
    <div className="divide-y">
      {toggles.map((toggle) => (
        <SwitchRow
          key={toggle.key}
          label={toggle.label}
          hint={toggle.hint}
          checked={settings[toggle.key] as boolean}
          disabled={disabled}
          onChange={(checked) => onChange({ [toggle.key]: checked } as Partial<TaskCardSettings>)}
        />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Router                                                                      */
/* -------------------------------------------------------------------------- */

/** Renders the editor panel for whichever card region is selected. */
export function TaskCardSectionEditor({ section, ...props }: SectionProps & { section: TaskCardSection }) {
  const { t } = useTranslation("back-office");

  switch (section) {
    case "identity":
      return <IdentitySection {...props} />;
    case "header":
      return <HeaderSection {...props} />;
    case "subHeader":
      return <SubHeaderSection {...props} />;
    case "topContent":
      return (
        <div className="space-y-3">
          {props.overriddenFields.includes("topContentFields") && (
            <Badge variant="secondary" className="font-normal">
              {t("tasks.editor.overridden")}
            </Badge>
          )}
          <FieldListEditor
            withGridSize
            fields={props.settings.topContentFields}
            disabled={props.disabled}
            onChange={(fields) => props.onChange({ topContentFields: fields as TopContentField[] })}
          />
        </div>
      );
    case "infoFields":
      return (
        <div className="space-y-3">
          {props.overriddenFields.includes("infoTabFields") && (
            <Badge variant="secondary" className="font-normal">
              {t("tasks.editor.overridden")}
            </Badge>
          )}
          <FieldListEditor
            withGridSize={false}
            fields={props.settings.infoTabFields}
            disabled={props.disabled}
            onChange={(fields) => props.onChange({ infoTabFields: fields as InfoTabField[] })}
          />
        </div>
      );
    case "tabs":
      return <TabsSection {...props} />;
    case "actions":
      return <ActionsSection {...props} />;
    case "snooze":
      return <SnoozeSection {...props} />;
    case "toggles":
      return <TogglesSection {...props} />;
    default:
      return null;
  }
}
